import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import {
  fetchScrapedJobs,
  loadMoreScrapedJobs,
  fetchMarkets,
  ScrapedJob,
  MetroMarket,
  ScrapedJobSort,
} from '../store/scrapedJobsSlice';
import { RootState, AppDispatch } from '../store';
import { FaMapMarkerAlt, FaFilter, FaBriefcase, FaCity, FaBookmark, FaRegBookmark } from 'react-icons/fa';
import { SkeletonJobList } from '../components/Skeleton';
import SEOHead from '../components/SEOHead';
import { fetchSavedIds, saveJob, unsaveJob } from '../store/savedJobsSlice';
import { fetchProfile } from '../store/workerProfileSlice';
import { trackEvent } from '../utils/analytics';
import WorkerDiscoveryNav from '../components/WorkerDiscoveryNav';
import { resolveLegacyRoleQuery } from '../utils/roleJobQueries';
import { getPastTitleDateWarning } from '../utils/listingDateSignals';
import { recordPendingContentDestination } from '../utils/firstPartyAnalytics';
import { formatCompactJobPay } from '../utils/jobPayPresentation';
import { getJobLocationLabel } from '../utils/jobLocationPresentation';
import { getJobSchedulePresentation } from '../utils/jobSchedulePresentation';
import { getOpenActivityCheckedAt, hasEmployerPublishedPay } from '../utils/jobActivityPresentation';
import { formatJobSource } from '../utils/jobSourcePresentation';

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'stagecrew', label: 'StageCrew Direct' },
  { value: 'greenhouse', label: 'Employer job board' },
  { value: 'workday', label: 'Employer job board (Workday)' },
  { value: 'encore', label: 'Employer job board (Encore)' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'freelance', label: 'Freelance / Gig' },
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
];

const SORT_OPTIONS: Array<{ value: ScrapedJobSort; label: string }> = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'newest', label: 'Newest available' },
];

type JobFilters = {
  search: string;
  location: string;
  source: string;
  employmentType: string;
  role: string;
  metro: string;
  sort: ScrapedJobSort;
};

type JobRequestFilters = {
  search?: string;
  location?: string;
  source?: string;
  employmentType?: string;
  role?: string;
  metro?: string;
  sort: ScrapedJobSort;
  is_active: boolean;
  limit: number;
  offset?: number;
};

const EMPTY_FILTERS: JobFilters = {
  search: '',
  location: '',
  source: '',
  employmentType: '',
  role: '',
  metro: '',
  sort: 'recommended',
};

const JOB_FILTER_QUERY_KEYS = [
  'search',
  'location',
  'source',
  'employmentType',
  'role',
  'minRate',
  'metro',
  'sort',
] as const;

function readQueryValue(searchParams: URLSearchParams, key: string, maxLength: number): string {
  return (searchParams.get(key) || '').trim().slice(0, maxLength);
}

function filtersFromSearchParams(searchParams: URLSearchParams): JobFilters {
  const requestedSource = readQueryValue(searchParams, 'source', 50).toLowerCase();
  const requestedEmploymentType = readQueryValue(searchParams, 'employmentType', 50).toLowerCase();
  const requestedSearch = readQueryValue(searchParams, 'search', 200);
  const requestedRole = readQueryValue(searchParams, 'role', 100);
  const legacyRoleQuery = resolveLegacyRoleQuery(requestedRole);
  const metro = readQueryValue(searchParams, 'metro', 50);
  const requestedSorts = searchParams.getAll('sort');
  const requestedSort = requestedSorts.length === 1
    ? requestedSorts[0].trim()
    : '';

  return {
    search: requestedSearch || (
      legacyRoleQuery?.param === 'search' ? legacyRoleQuery.value : ''
    ),
    location: metro ? '' : readQueryValue(searchParams, 'location', 200),
    source: SOURCE_OPTIONS.some(option => option.value === requestedSource)
      ? requestedSource
      : '',
    employmentType: EMPLOYMENT_TYPE_OPTIONS.some(option => option.value === requestedEmploymentType)
      ? requestedEmploymentType
      : '',
    role: legacyRoleQuery
      ? (legacyRoleQuery.param === 'role' ? legacyRoleQuery.value : '')
      : requestedRole,
    metro,
    sort: SORT_OPTIONS.some(option => option.value === requestedSort)
      ? requestedSort as ScrapedJobSort
      : 'recommended',
  };
}

function normalizeRoleForApi(role: string): string {
  return role
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function formatRoleFilter(role: string): string {
  return role
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function buildFilterParams(filters: JobFilters): JobRequestFilters {
  const params: JobRequestFilters = {
    is_active: true,
    limit: 50,
    sort: filters.sort,
  };
  const normalizedRole = normalizeRoleForApi(filters.role);

  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.metro.trim()) {
    params.metro = filters.metro.trim();
  } else if (filters.location.trim()) {
    params.location = filters.location.trim();
  }
  if (filters.source) params.source = filters.source;
  if (filters.employmentType) params.employmentType = filters.employmentType;
  if (normalizedRole) params.role = normalizedRole;

  return params;
}

function searchParamsFromFilters(
  currentSearchParams: URLSearchParams,
  filters: JobFilters,
): URLSearchParams {
  const nextSearchParams = new URLSearchParams(currentSearchParams);
  JOB_FILTER_QUERY_KEYS.forEach(key => nextSearchParams.delete(key));

  if (filters.search.trim()) nextSearchParams.set('search', filters.search.trim());
  if (filters.metro.trim()) {
    nextSearchParams.set('metro', filters.metro.trim());
  } else if (filters.location.trim()) {
    nextSearchParams.set('location', filters.location.trim());
  }
  if (filters.source) nextSearchParams.set('source', filters.source);
  if (filters.employmentType) nextSearchParams.set('employmentType', filters.employmentType);
  if (filters.role.trim()) nextSearchParams.set('role', filters.role.trim());
  if (filters.sort === 'newest') nextSearchParams.set('sort', filters.sort);

  return nextSearchParams;
}

function jobsPathFromFilters(filters: JobFilters): string {
  const query = searchParamsFromFilters(new URLSearchParams(), filters).toString();
  return query ? `/jobs?${query}` : '/jobs';
}

function formatRate(job: ScrapedJob): string {
  const hasMinimum = job.rate_min !== null && job.rate_min !== undefined;
  const hasMaximum = job.rate_max !== null && job.rate_max !== undefined;
  return hasMinimum || hasMaximum || job.rate
    ? formatCompactJobPay(job)
    : '';
}

function formatRoles(roles: string[]): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles.map(r => r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHrs < 1) return 'Just now';
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

function sourceColor(source: string): string {
  switch (source) {
    case 'stagecrew': return 'bg-blue-600 text-white';
    case 'linkedin': return 'bg-blue-100 text-blue-800';
    case 'craigslist': return 'bg-purple-100 text-purple-800';
    case 'indeed': return 'bg-indigo-100 text-indigo-800';
    case 'simplyhired': return 'bg-teal-100 text-teal-800';
    case 'staffmeup': return 'bg-green-100 text-green-800';
    case 'entertainmentcareers': return 'bg-orange-100 text-orange-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

function employmentTypeBadge(type: string | null): { label: string; className: string } | null {
  switch (type) {
    case 'freelance': return { label: 'Freelance Gig', className: 'bg-emerald-100 text-emerald-800' };
    case 'full_time': return { label: 'Full-Time', className: 'bg-sky-100 text-sky-800' };
    case 'part_time': return { label: 'Part-Time', className: 'bg-amber-100 text-amber-800' };
    default: return null;
  }
}

const BASE_URL = 'https://stagecrew.app';

const JobsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { jobs, loading, loadingMore, hasMore, error, markets } = useSelector((state: RootState) => state.scrapedJobs);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { savedIds, idsLoading } = useSelector((state: RootState) => state.savedJobs);
  const {
    profile,
    loading: profileLoading,
    error: profileError,
  } = useSelector((state: RootState) => state.workerProfile);
  const canSaveJobs = isAuthenticated && user?.role === 'worker';
  const missingPrimaryRole = Boolean(profile) && !profile?.primary_role?.trim();
  const missingLocation = Boolean(profile) && !profile?.location?.trim();
  const matchingPreferencePrompt = useMemo(() => (
    !canSaveJobs || (!missingPrimaryRole && !missingLocation)
      ? null
      : missingPrimaryRole && missingLocation
        ? {
            heading: 'Add your primary role and city',
            detail: 'those two details',
            cta: 'Set role & city',
          }
        : missingPrimaryRole
          ? {
              heading: 'Add your primary role',
              detail: 'your primary role',
              cta: 'Set primary role',
            }
          : {
              heading: 'Add your city',
              detail: 'your city',
              cta: 'Set city',
            }
  ), [canSaveJobs, missingLocation, missingPrimaryRole]);
  const [searchParams, setSearchParams] = useSearchParams();
  const committedFilters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams],
  );
  const canonicalSearchParams = useMemo(
    () => searchParamsFromFilters(searchParams, committedFilters),
    [committedFilters, searchParams],
  );
  const currentJobsPath = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/jobs?${query}` : '/jobs';
  }, [searchParams]);
  const [filters, setFilters] = useState<JobFilters>(committedFilters);
  const [savePendingIds, setSavePendingIds] = useState<number[]>([]);
  const [saveError, setSaveError] = useState('');
  const [showQualificationDetails, setShowQualificationDetails] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showMarkets, setShowMarkets] = useState(false);
  const secondaryFilterCount = Number(Boolean(filters.source))
    + Number(Boolean(filters.employmentType));
  const savePendingIdsRef = useRef(new Set<number>());
  const activationPromptTrackedRef = useRef(false);
  const profileFetchAttemptedForUserRef = useRef<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const committedQueryRef = useRef(searchParams.toString());
  committedQueryRef.current = searchParams.toString();

  useEffect(() => {
    recordPendingContentDestination('jobs');
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setFilters(committedFilters);
    if (canonicalSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(canonicalSearchParams, { replace: true });
      return;
    }
    dispatch(fetchScrapedJobs(buildFilterParams(committedFilters)));
  }, [
    canonicalSearchParams,
    committedFilters,
    dispatch,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    dispatch(fetchMarkets());
  }, [dispatch]);

  useEffect(() => {
    if (canSaveJobs) dispatch(fetchSavedIds());
  }, [dispatch, canSaveJobs]);

  useEffect(() => {
    if (!canSaveJobs) {
      profileFetchAttemptedForUserRef.current = null;
      return;
    }
    if (
      user?.id
      && !profile
      && !profileLoading
      && profileFetchAttemptedForUserRef.current !== user.id
    ) {
      profileFetchAttemptedForUserRef.current = user.id;
      dispatch(fetchProfile());
    }
  }, [canSaveJobs, dispatch, profile, profileError, profileLoading, user?.id]);

  useEffect(() => {
    if (matchingPreferencePrompt && !activationPromptTrackedRef.current) {
      activationPromptTrackedRef.current = true;
      trackEvent('jobs_activation_prompt_viewed', 'activation', 'jobs');
    }
  }, [matchingPreferencePrompt]);

  const handleToggleSave = async (e: React.MouseEvent, jobId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canSaveJobs || idsLoading || savePendingIdsRef.current.has(jobId)) return;

    const wasSaved = savedIds.includes(jobId);
    savePendingIdsRef.current.add(jobId);
    setSavePendingIds(Array.from(savePendingIdsRef.current));
    setSaveError('');
    try {
      if (wasSaved) {
        await dispatch(unsaveJob(jobId)).unwrap();
      } else {
        await dispatch(saveJob(jobId)).unwrap();
      }
    } catch {
      setSaveError(
        wasSaved
          ? 'Could not remove this saved job. Try again.'
          : 'Could not save this job. Try again.',
      );
    } finally {
      savePendingIdsRef.current.delete(jobId);
      setSavePendingIds(Array.from(savePendingIdsRef.current));
    }
  };

  // Dynamic SEO based on active filters
  const seo = useMemo(() => {
    const activeMetro = markets.find((m: MetroMarket) => m.key === committedFilters.metro);
    const activeSource = SOURCE_OPTIONS.find(s => s.value === committedFilters.source && s.value);

    let title = 'AV & Stagehand Jobs';
    let description = 'Browse pay-disclosed AV technician, stagehand, and production crew jobs recently confirmed on employer-controlled sources.';
    const urlPath = jobsPathFromFilters(committedFilters);

    if (activeMetro && activeSource) {
      title = `${activeSource.label} AV Jobs in ${activeMetro.name}`;
      description = `Browse pay-disclosed AV and stagehand jobs in ${activeMetro.displayName} from ${activeSource.label} that meet StageCrew’s current-source checks.`;
    } else if (activeMetro) {
      title = `AV & Stagehand Jobs in ${activeMetro.name}`;
      description = `${activeMetro.jobCount} pay-disclosed AV technician and stagehand jobs in ${activeMetro.displayName} that meet StageCrew’s current-source checks.`;
    } else if (activeSource) {
      title = `${activeSource.label} AV & Stagehand Jobs`;
      description = `Browse pay-disclosed AV technician and stagehand jobs from ${activeSource.label} that meet StageCrew’s current-source checks.`;
    }

    title += ' | StageCrew';

    const jsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title.replace(' | StageCrew', ''),
      description,
      url: `${BASE_URL}${urlPath}`,
      provider: {
        '@type': 'Organization',
        name: 'StageCrew',
        url: BASE_URL,
      },
    };

    // Add ItemList for the current job listings
    if (jobs.length > 0) {
      jsonLd.mainEntity = {
        '@type': 'ItemList',
        numberOfItems: jobs.length,
        itemListElement: jobs.slice(0, 20).map((job: ScrapedJob, i: number) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${BASE_URL}/jobs/${job.id}`,
          name: job.title,
        })),
      };
    }

    return { title, description, url: `${BASE_URL}${urlPath}`, canonical: `${BASE_URL}/jobs`, jsonLd };
  }, [committedFilters, markets, jobs]);

  // Debounce search input — auto-submit after 400ms of no typing
  const commitFiltersToUrl = useCallback((nextFilters: JobFilters, refreshIfUnchanged = false) => {
    const nextSearchParams = searchParamsFromFilters(searchParams, nextFilters);
    if (nextSearchParams.toString() === searchParams.toString()) {
      if (refreshIfUnchanged) {
        dispatch(fetchScrapedJobs(buildFilterParams(nextFilters)));
      }
      return;
    }
    setSearchParams(nextSearchParams, { replace: true });
  }, [dispatch, searchParams, setSearchParams]);

  const debouncedSearch = useCallback((newFilters: JobFilters) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const scheduledFromQuery = searchParams.toString();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      if (committedQueryRef.current !== scheduledFromQuery) return;
      commitFiltersToUrl(newFilters);
    }, 400);
  }, [commitFiltersToUrl, searchParams]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    if (e.target.type === 'text') {
      debouncedSearch(newFilters);
    } else {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      commitFiltersToUrl(newFilters);
    }
  };

  const handleMetroSelect = (metroKey: string) => {
    const newMetro = filters.metro === metroKey ? '' : metroKey;
    const nextFilters = { ...filters, metro: newMetro, location: '' };
    setFilters(nextFilters);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    commitFiltersToUrl(nextFilters);
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    commitFiltersToUrl(filters, true);
  };

  const handleLoadMore = () => {
    const params = buildFilterParams(committedFilters);
    params.offset = jobs.length;
    dispatch(loadMoreScrapedJobs(params));
  };

  const clearRoleFilter = () => {
    const nextFilters = { ...filters, role: '' };
    setFilters(nextFilters);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    commitFiltersToUrl(nextFilters);
  };

  const clearAllFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    commitFiltersToUrl(EMPTY_FILTERS);
  };

  const mobileResultSummary = jobs.length > 0
    ? `${jobs.length} currently qualified ${jobs.length === 1 ? 'job' : 'jobs'} shown${hasMore ? ' · More available' : ''}`
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={seo.title}
        description={seo.description}
        url={seo.url}
        canonical={seo.canonical}
        jsonLd={seo.jsonLd}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <WorkerDiscoveryNav />
        <div className="mb-5 lg:mb-8">
          <h1 className="text-4xl font-bold mb-2">AV & Stagehand Jobs</h1>
          <p className="text-gray-600">
            Every listing shown includes a concrete amount and pay period.
          </p>
          <button
            type="button"
            className="mt-2 inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 lg:hidden"
            aria-expanded={showQualificationDetails}
            aria-controls="job-qualification-details"
            onClick={() => setShowQualificationDetails(current => !current)}
          >
            How listings qualify
            <span className="ml-2" aria-hidden="true">
              {showQualificationDetails ? '−' : '+'}
            </span>
          </button>
          <div
            id="job-qualification-details"
            className={`${showQualificationDetails ? 'block' : 'hidden'} mt-2 lg:block`}
          >
            <p className="text-gray-600">
              External listings must also have been listed as open on an approved employer-controlled source within the last 12 hours.
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Availability can change after a check. Review the original employer listing before applying.
            </p>
          </div>
        </div>

        {matchingPreferencePrompt && (
          <section
            aria-labelledby="matching-preferences-prompt-title"
            className="mb-8 rounded-xl border border-blue-200 bg-blue-50 p-5 sm:flex sm:items-center sm:justify-between sm:gap-6"
          >
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">
                Improve your matches
              </p>
              <h2
                id="matching-preferences-prompt-title"
                className="mt-1 text-xl font-bold text-gray-900"
              >
                {matchingPreferencePrompt.heading}
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-700">
                StageCrew uses {matchingPreferencePrompt.detail} to rank listings against your preferences.
                You can keep browsing, saving, and applying manually.
              </p>
            </div>
            <Link
              to="/profile/setup"
              state={{ from: '/jobs', onboarding: true }}
              onClick={() => trackEvent('jobs_activation_prompt_clicked', 'activation', 'jobs')}
              className="mt-4 inline-flex shrink-0 items-center justify-center rounded-lg bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 sm:mt-0"
            >
              {matchingPreferencePrompt.cta}
            </Link>
          </section>
        )}

        {!loading && !error && jobs.length > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 lg:hidden">
            <p className="min-w-0 text-sm font-medium text-gray-700" aria-live="polite">
              {mobileResultSummary}
            </p>
            <a
              href="#job-results"
              className="inline-flex min-h-11 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              View jobs
            </a>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white p-4 sm:p-6 rounded shadow sticky top-4">
              <div className="flex items-center mb-4">
                <FaFilter className="mr-2" />
                <h3 className="text-lg font-bold">Filters</h3>
              </div>

              <form onSubmit={handleFilterSubmit} className="space-y-4 [&_input]:min-h-11 [&_select]:min-h-11">
                {filters.role && (
                  <div
                    className="rounded-lg border border-blue-200 bg-blue-50 p-3"
                    aria-label="Active linked filters"
                  >
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
                      Active filters
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={clearRoleFilter}
                        className="inline-flex min-h-11 items-center rounded-full bg-white px-3 py-1 text-sm font-medium text-blue-800 shadow-sm hover:bg-blue-100"
                        aria-label="Clear role filter"
                      >
                        Role: {formatRoleFilter(filters.role)} <span aria-hidden="true">×</span>
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="filter-search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <input
                    id="filter-search"
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Keywords..."
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>

                <div>
                  <label htmlFor="filter-location" className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  {filters.metro ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded bg-blue-50 text-blue-700 text-sm">
                      {markets.find((m: MetroMarket) => m.key === filters.metro)?.displayName || filters.metro}
                      <button
                        type="button"
                        onClick={() => handleMetroSelect('')}
                        className="ml-2 inline-flex min-h-11 min-w-11 items-center justify-center text-blue-500 hover:text-blue-700"
                        aria-label="Clear location filter"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <input
                      id="filter-location"
                      type="text"
                      name="location"
                      value={filters.location}
                      onChange={handleFilterChange}
                      placeholder="City or state..."
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    />
                  )}
                </div>

                <div>
                  <label htmlFor="filter-sort" className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                  <select
                    id="filter-sort"
                    name="sort"
                    value={filters.sort}
                    onChange={handleFilterChange}
                    aria-describedby="filter-sort-description"
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    {SORT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <p id="filter-sort-description" className="sr-only lg:not-sr-only lg:mt-1 lg:text-xs lg:leading-5 lg:text-gray-500">
                    {filters.sort === 'newest'
                      ? 'Uses the earliest StageCrew record among each listing’s currently available copies, not the source’s original posting date.'
                      : 'Recommended starts with paid featured StageCrew listings, then weighs source-location clarity, listing detail, and recency.'}
                  </p>
                </div>

                <button
                  type="button"
                  className="flex min-h-[44px] w-full items-center justify-between rounded border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700 lg:hidden"
                  aria-expanded={showMoreFilters}
                  aria-controls="secondary-job-filters"
                  onClick={() => setShowMoreFilters(current => !current)}
                >
                  <span>
                    More filters{secondaryFilterCount > 0 ? ` (${secondaryFilterCount} active)` : ''}
                  </span>
                  <span aria-hidden="true">{showMoreFilters ? '−' : '+'}</span>
                </button>

                <div
                  id="secondary-job-filters"
                  className={`${showMoreFilters ? 'space-y-4' : 'hidden'} lg:block lg:space-y-4`}
                >
                  <div>
                    <label htmlFor="filter-source" className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      id="filter-source"
                      name="source"
                      value={filters.source}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    >
                      {SOURCE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="filter-employment-type" className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                    <select
                      id="filter-employment-type"
                      name="employmentType"
                      value={filters.employmentType}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    >
                      {EMPLOYMENT_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  className="min-h-11 w-full rounded bg-blue-600 py-2 text-white hover:bg-blue-700"
                >
                  Apply Filters
                </button>
              </form>

              {markets.length > 0 && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    className="flex min-h-[44px] w-full items-center justify-between rounded border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700 lg:hidden"
                    aria-expanded={showMarkets}
                    aria-controls="job-market-filters"
                    onClick={() => setShowMarkets(current => !current)}
                  >
                    <span>Browse markets</span>
                    <span aria-hidden="true">{showMarkets ? '−' : '+'}</span>
                  </button>
                  <div
                    id="job-market-filters"
                    className={`${showMarkets ? 'block' : 'hidden'} mt-3 lg:block`}
                  >
                    <div className="mb-3 flex items-center gap-2">
                      <FaCity className="text-gray-500" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Markets</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {markets.map((market: MetroMarket, index) => (
                        <button
                          type="button"
                          key={`${market.key || market.name}-${index}`}
                          onClick={() => handleMetroSelect(market.key)}
                          className={`min-h-11 rounded-lg px-3 py-2 text-sm font-medium transition ${
                            filters.metro === market.key
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'border border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          <span>{market.name}</span>
                          {market.jobCount > 0 && (
                            <span className={`ml-1.5 text-xs ${
                              filters.metro === market.key ? 'text-blue-200' : 'text-gray-400'
                            }`}>
                              {market.jobCount}
                            </span>
                          )}
                        </button>
                      ))}
                      {filters.metro && (
                        <button
                          type="button"
                          onClick={() => handleMetroSelect('')}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Jobs List */}
          <section
            id="job-results"
            tabIndex={-1}
            aria-label="Qualified job results"
            className="scroll-mt-4 focus:outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 lg:col-span-3"
          >
            {saveError && (
              <p
                className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
              >
                {saveError}
              </p>
            )}
            {loading ? (
              <SkeletonJobList count={5} />
            ) : error ? (
              <div className="bg-red-50 border border-red-200 p-6 rounded text-center">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={() => dispatch(fetchScrapedJobs(buildFilterParams(committedFilters)))}
                  className="mt-4 text-blue-600 hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : jobs.length > 0 ? (
              <>
                <p className="mb-4 hidden text-sm text-gray-500 lg:block" aria-live="polite">
                  Showing {jobs.length} currently qualified {jobs.length === 1 ? 'job' : 'jobs'}
                  {hasMore ? ' · More available' : ''}
                </p>
                <div className="space-y-4">
                  {jobs.map((job: ScrapedJob) => {
                    const rateLabel = formatRate(job);
                    const openActivityCheckedAt = getOpenActivityCheckedAt(job);
                    const schedulePresentation = getJobSchedulePresentation(job.work_schedule);
                    const pastTitleDateWarning = schedulePresentation.cardText
                      ? null
                      : getPastTitleDateWarning(job.title);
                    const titleId = `job-card-${job.id}-title`;
                    const detailsId = `job-card-${job.id}-details`;
                    return (
                      <article
                        key={job.id}
                        data-testid={`job-card-${job.id}`}
                        className={`relative overflow-hidden rounded shadow transition-shadow hover:shadow-lg ${
                          job.is_featured
                            ? 'border-2 border-amber-400 bg-amber-50 ring-1 ring-amber-200'
                            : 'bg-white'
                        }`}
                      >
                      <Link
                        to={`/jobs/${job.id}`}
                        state={{ jobsReturnPath: currentJobsPath }}
                        aria-labelledby={`${titleId} ${detailsId}`}
                        className="group block rounded p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:p-6"
                      >
                        <div>
                          <div className="flex flex-wrap items-start gap-2">
                            <h3
                              id={titleId}
                              className="min-w-0 text-xl font-bold leading-snug text-blue-700 group-hover:underline"
                            >
                              {job.title}
                            </h3>
                            {job.is_featured && (
                              <span className="rounded-full bg-amber-200 px-2.5 py-1 text-xs font-bold text-amber-900">
                                Featured listing
                              </span>
                            )}
                          </div>
                          {job.company && (
                            <p className="mt-1 font-medium text-gray-800">{job.company}</p>
                          )}
                        </div>

                        <div className="mt-3 flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-6">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-xl font-bold text-green-700">
                              {rateLabel || 'Pay qualification unavailable'}
                            </span>
                            {rateLabel && hasEmployerPublishedPay(job) && (
                              <span className="text-xs font-medium text-gray-600">
                                Employer-published pay
                              </span>
                            )}
                          </div>
                          <div className="flex min-w-0 items-center text-gray-700">
                            <FaMapMarkerAlt className="mr-1.5 flex-shrink-0" aria-hidden="true" />
                            <span>{getJobLocationLabel(job)}</span>
                          </div>
                        </div>

                        {schedulePresentation.cardText && (
                          <p className={`mt-3 text-sm font-medium ${
                            schedulePresentation.caution
                              ? 'rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900'
                              : 'text-gray-700'
                          }`}>
                            {schedulePresentation.cardText}
                          </p>
                        )}
                        {pastTitleDateWarning && (
                          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                            Date check: {pastTitleDateWarning}
                          </p>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          {formatRoles(job.roles_needed).map((role, i) => (
                            <span key={i} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium">
                              {role}
                            </span>
                          ))}
                          {job.job_type && job.job_type !== 'other' && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                              <FaBriefcase className="inline mr-1" />
                              {job.job_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                          )}
                          {job.certifications_required && job.certifications_required.length > 0 &&
                            job.certifications_required.map((cert, i) => (
                              <span key={`cert-${i}`} className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                                {cert}
                              </span>
                            ))
                          }
                        </div>

                        <div className="mt-4 border-t border-gray-200 pt-3 text-xs text-gray-600">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`px-2 py-1 rounded font-medium ${sourceColor(job.source)}`}>
                              Original source: {formatJobSource(job.source)}
                            </span>
                            {employmentTypeBadge(job.employment_type) && (
                              <span className={`px-2 py-1 rounded font-medium ${employmentTypeBadge(job.employment_type)!.className}`}>
                                {employmentTypeBadge(job.employment_type)!.label}
                              </span>
                            )}
                            {job.union_required && job.union_required !== 'either' && (
                              <span className="text-gray-600">
                                {(job.union_required as string) === 'iatse' ? 'IATSE' : 'Non-Union'}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex items-end justify-between gap-3">
                            <p className="min-w-0 leading-relaxed">
                              {openActivityCheckedAt ? (
                                <>
                                  <span className="font-semibold text-green-700">Source showed open</span>
                                  {' · '}Checked {timeAgo(openActivityCheckedAt)}
                                </>
                              ) : (
                                job.source === 'stagecrew'
                                  ? `Posted on StageCrew ${timeAgo(job.scraped_at)}`
                                  : 'Current-source check unavailable'
                              )}
                            </p>
                            <span
                              id={detailsId}
                              className="inline-flex flex-shrink-0 items-center font-semibold text-blue-700 group-hover:underline"
                            >
                              View job details <span className="ml-1" aria-hidden="true">→</span>
                            </span>
                          </div>
                        </div>
                      </Link>
                      {canSaveJobs && (
                        <div className="flex justify-end border-t border-gray-200 px-5 py-2 sm:px-6">
                          <button
                            type="button"
                            onClick={(event) => handleToggleSave(event, job.id)}
                            disabled={idsLoading || savePendingIds.includes(job.id)}
                            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-1 transition-colors hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            aria-label={
                              idsLoading
                                ? 'Loading saved job status'
                                : savePendingIds.includes(job.id)
                                ? savedIds.includes(job.id) ? 'Removing saved job' : 'Saving job'
                                : savedIds.includes(job.id) ? 'Unsave job' : 'Save job'
                            }
                            title={savedIds.includes(job.id) ? 'Remove from saved' : 'Save for later'}
                          >
                            {savedIds.includes(job.id) ? (
                              <FaBookmark className="text-blue-600" />
                            ) : (
                              <FaRegBookmark className="text-gray-400" />
                            )}
                          </button>
                        </div>
                      )}
                      </article>
                    );
                  })}
                </div>
                {hasMore && (
                  <div className="text-center mt-6">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="bg-white text-blue-600 border border-blue-300 px-8 py-3 rounded-lg font-medium hover:bg-blue-50 transition disabled:opacity-50"
                    >
                      {loadingMore ? 'Loading...' : 'Load More Jobs'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white p-12 rounded text-center">
                <FaBriefcase className="inline-block text-5xl text-gray-300 mb-4" />
                <p className="text-gray-600 text-lg mb-2">No currently qualified jobs found</p>
                {(filters.search || filters.metro || filters.source || filters.location || filters.employmentType || filters.role || filters.sort !== 'recommended') ? (
                  <>
                    <p className="text-gray-500 text-sm mb-4">No pay-disclosed, recently confirmed listings match these filters. Try another market or remove filters.</p>
                    <button
                      onClick={clearAllFilters}
                      className="inline-flex min-h-11 items-center text-sm font-medium text-blue-600 hover:underline"
                    >
                      Clear all filters
                    </button>
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">StageCrew currently has no listings that meet both its pay and availability checks. Check back after the next employer-board refresh.</p>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
